import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import ShellSaac, { useVoltar } from '../../../components/shell/ShellSaac';
import {
  Botao,
  Chip,
  DataList,
  EmptyState,
  ErrorState,
  Field,
  Nota,
  PageHeader,
  Pagination,
  SearchInput,
  Select,
  Skeleton,
} from '../../../ui';

import ModalDistrato from '../../../components/modal/distrato/ModalDistrato';

import { TI } from '../routes';
import {
  ContratoLista,
  ListaContratos,
  abrirPdfContrato,
  cancelarContrato,
  listarContratos,
} from '../../../api/contrato/contratoApi';
import { formatCpfCnpj } from '../../../utils/formartCPF';
import { formatarDataCurta } from '../../../utils/data';
import { rotuloStatusContrato } from '../../../utils/contratoStatus';
import { extrairMensagemErro } from '../../../utils/apiError';

/**
 * Acompanhamento de contratos (at-40, item 43; reescrito na at-69, frente D/D7).
 *
 * ⚠️ **O escopo é do BACKEND**: TI/ADMIN vê tudo, consultor vê a própria carteira.
 * A tela não decide isso nem esconde linha por papel — mostra o que recebeu. Isso
 * vira texto na `Nota` do topo, porque é a **mesma rota** que o item 61 vai expor
 * ao consultor, e quem abre precisa saber o que está vendo.
 *
 * ## O que mudou na at-69
 *
 *   - **Erro deixou de ser mudo**: era `toast.error` (que passa) com a tabela
 *     mostrando "Nenhum contrato encontrado." (que fica) — o TI concluía que não
 *     havia contrato. Agora a lista é **descartada** e aparece o `ErrorState`.
 *   - **Três vazios distintos** (sem contratos / a busca não achou / o filtro não
 *     casou), que eram um texto só.
 *   - **Contadores globais com a ressalva em TEXTO** — estava num `title`, que não
 *     existe no celular (at-65).
 *   - **Sem PDF** virava um botão apagado com a explicação em `title`; virou chip.
 *   - **"Ver PDF" mostra carregamento NA LINHA** que foi clicada.
 *   - Fim do `Math.ceil` na tela (o `Pagination` calcula), do `FullScreenLoader`,
 *     do debounce escrito à mão e da `<table>` que não colapsava no celular.
 */

type Estado = 'carregando' | 'ok' | 'erro';

/** As 4 opções são as mesmas de sempre (D7) — um filtro só não justifica modal. */
const OPCOES_STATUS = [
  { valor: 'TODOS', rotulo: 'Todos os status' },
  { valor: 'EMITIDO', rotulo: 'Aguardando assinatura' },
  { valor: 'ASSINADO', rotulo: 'Assinados' },
  { valor: 'CANCELADO', rotulo: 'Cancelados' },
];

const ContratosPage: React.FC = () => {
  const voltar = useVoltar(TI());

  const [dados, setDados] = useState<ListaContratos | null>(null);
  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState('');

  const [pagina, setPagina] = useState(1);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('TODOS');
  const [chaveBusca, setChaveBusca] = useState(0);

  const [paraCancelar, setParaCancelar] = useState<ContratoLista | null>(null);
  const [cancelando, setCancelando] = useState(false);
  /** Id da linha cujo PDF está sendo baixado — o carregamento é POR LINHA. */
  const [abrindoPdf, setAbrindoPdf] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setEstado('carregando');
    try {
      setDados(await listarContratos({ pagina, busca, status }));
      setEstado('ok');
    } catch (falha) {
      // 🔴 A lista é DESCARTADA no erro: manter a anterior faria dado velho passar
      // por atual, e aqui o TI decide sobre um documento a partir do que vê.
      setDados(null);
      setErro(extrairMensagemErro(falha, 'Não foi possível carregar os contratos.'));
      setEstado('erro');
    }
  }, [pagina, busca, status]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const trocarBusca = (termo: string) => {
    setBusca(termo);
    setPagina(1);
  };

  const limparBusca = () => {
    setBusca('');
    setPagina(1);
    setChaveBusca((c) => c + 1);
  };

  const abrirPdf = async (contrato: ContratoLista) => {
    setAbrindoPdf(contrato.id);
    try {
      const abriu = await abrirPdfContrato(contrato.id);
      // Pop-up bloqueado não é falha — é configuração do navegador. Desde a at-81
      // o PDF é baixado nesse caso; o aviso diz para onde ele foi.
      if (!abriu) {
        toast.info('O navegador bloqueou a aba nova — o contrato foi baixado.');
      }
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Não foi possível abrir o contrato.'));
    } finally {
      setAbrindoPdf(null);
    }
  };

  const confirmarCancelamento = async (motivo: string) => {
    if (!paraCancelar) return;

    setCancelando(true);
    try {
      await cancelarContrato(paraCancelar.id, motivo);
      toast.success('Contrato cancelado.');
      setParaCancelar(null);
      await carregar();
    } catch (falha) {
      toast.error(extrairMensagemErro(falha, 'Não foi possível cancelar o contrato.'));
    } finally {
      setCancelando(false);
    }
  };

  const colunas = [
    {
      id: 'associado',
      rotulo: 'Associado',
      principal: true,
      celula: (c: ContratoLista) => c.associado ?? 'Associado não identificado',
    },
    {
      id: 'cpf',
      rotulo: 'CPF / CNPJ',
      secundaria: true,
      celula: (c: ContratoLista) => (c.cpfCnpj ? formatCpfCnpj(c.cpfCnpj) : '—'),
    },
    {
      id: 'veiculo',
      rotulo: 'Veículo',
      celula: (c: ContratoLista) => (
        <span>
          {c.placa ?? '—'}
          {c.modelo && (
            <span className="tw-block tw-text-xs tw-text-texto-suave">{c.modelo}</span>
          )}
        </span>
      ),
    },
    {
      id: 'contrato',
      rotulo: 'Contrato',
      celula: (c: ContratoLista) => c.titulo ?? `Tipo ${c.codigoTipo ?? '—'}`,
    },
    {
      id: 'situacao',
      rotulo: 'Situação',
      celula: (c: ContratoLista) => {
        const rotulo = rotuloStatusContrato(c.status);
        return (
          <span>
            <Chip variante={rotulo.variante}>{rotulo.texto}</Chip>
            {/* O motivo do distrato em texto, não em `title`: é a justificativa
                de uma decisão e alguém vai precisar lê-la no celular. */}
            {c.motivoCancelamento && (
              <span className="tw-mt-1 tw-block tw-text-xs tw-text-texto-suave">
                {c.motivoCancelamento}
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'gerado',
      rotulo: 'Gerado',
      celula: (c: ContratoLista) => formatarDataCurta(c.geradoEm),
    },
    {
      id: 'assinado',
      rotulo: 'Assinado',
      ocultarNoCard: true,
      celula: (c: ContratoLista) => formatarDataCurta(c.assinadoEm),
    },
  ];

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={5} altura="tw-h-14" />;

    if (estado === 'erro') {
      return (
        <ErrorState
          mensagem="Não foi possível carregar os contratos."
          detalhe={erro}
          aoTentarNovamente={() => void carregar()}
        />
      );
    }

    if (!dados || dados.contratos.length === 0) {
      // Os três vazios pedem ações diferentes (spec 02 §2.7).
      if (busca) {
        return (
          <EmptyState
            icone="busca"
            titulo="Nenhum contrato encontrado para essa busca"
            descricao={`A busca cobre nome, CPF e placa. Nada corresponde a "${busca}".`}
            acao={
              <Botao variante="secundario" onClick={limparBusca}>
                Limpar a busca
              </Botao>
            }
          />
        );
      }

      if (status !== 'TODOS') {
        return (
          <EmptyState
            titulo="Nenhum contrato nesta situação"
            descricao="Nenhum documento casa com o recorte escolhido."
            acao={
              <Botao
                variante="secundario"
                onClick={() => {
                  setStatus('TODOS');
                  setPagina(1);
                }}
              >
                Ver todos os status
              </Botao>
            }
          />
        );
      }

      return (
        <EmptyState
          icone="sem-dados"
          titulo="Nenhum contrato emitido"
          descricao="Os contratos aparecem aqui assim que o consultor gera o termo na adesão."
        />
      );
    }

    return (
      <>
        <DataList
          itens={dados.contratos}
          colunas={colunas}
          chave={(c) => c.id}
          rotuloAcessivel="Contratos emitidos"
          acoes={(c) => (
            <>
              {c.temPdf ? (
                <Botao
                  variante="secundario"
                  tamanho="sm"
                  carregando={abrindoPdf === c.id}
                  onClick={() => void abrirPdf(c)}
                >
                  Ver PDF
                </Botao>
              ) : (
                /* O motivo era um `title` num botão apagado — invisível onde mais
                   importa. Chip diz o estado; não há ação a oferecer. */
                <Chip variante="neutro">Sem PDF arquivado</Chip>
              )}

              {c.status !== 'CANCELADO' && (
                <Botao variante="perigo" tamanho="sm" onClick={() => setParaCancelar(c)}>
                  Cancelar
                </Botao>
              )}
            </>
          )}
        />

        <div className="tw-mt-4">
          <Pagination
            pagina={pagina}
            totalItens={dados.total}
            porPagina={dados.limite}
            aoTrocar={setPagina}
          />
        </div>
      </>
    );
  };

  return (
    <ShellSaac
      cabecalho={
        <PageHeader titulo="Contratos" sobretitulo="Administração (TI)" aoVoltar={voltar} />
      }
    >
      <div className="tw-min-h-full tw-bg-shell">
        <div className="tw-mx-auto tw-w-full tw-max-w-6xl md:tw-p-6">
          <div className="tw-bg-white tw-p-4 md:tw-rounded-2xl md:tw-border md:tw-border-superficie-borda md:tw-p-6 md:tw-shadow-[0_2px_12px_rgba(0,0,0,0.09)]">
            <div className="tw-flex tw-flex-col tw-gap-4">
              <Nota>
                Todos os contratos gerados pelo SAAC. <strong>TI e Administrador</strong> veem a
                associação inteira; um consultor vê apenas os da própria carteira. Cancelar exige
                um motivo e <strong>não apaga nada</strong> — o documento continua guardado.
              </Nota>

              <div className="tw-flex tw-flex-wrap tw-items-end tw-gap-3">
                <SearchInput
                  key={chaveBusca}
                  valorInicial={busca}
                  placeholder="Buscar por nome, CPF ou placa"
                  rotuloAcessivel="Buscar contrato"
                  className="tw-min-w-0 tw-flex-1 tw-basis-64"
                  aoBuscar={trocarBusca}
                />

                <Field rotulo="Situação" className="tw-w-full sm:tw-w-56" campo="status">
                  <Select
                    opcoes={OPCOES_STATUS}
                    /* Sem placeholder: "Todos os status" JÁ é a opção neutra. */
                    placeholder=""
                    value={status}
                    onChange={(evento) => {
                      setStatus(evento.target.value);
                      setPagina(1);
                    }}
                  />
                </Field>
              </div>

              {dados && (
                <div>
                  {/* Os totais são GLOBAIS e não seguem busca nem filtro (convenção
                      at-10/at-28). O aviso vai em texto — era um `title`. */}
                  <p className="tw-m-0 tw-mb-1.5 tw-text-[11px] tw-uppercase tw-tracking-wide tw-text-texto-desabilitado">
                    Totais gerais — não mudam com a busca nem com o filtro
                  </p>
                  <div className="tw-flex tw-flex-wrap tw-gap-1.5">
                    <Chip variante="pendente">{dados.totais.emitidos} aguardando assinatura</Chip>
                    <Chip variante="ok">{dados.totais.assinados} assinados</Chip>
                    <Chip variante="perigo">{dados.totais.cancelados} cancelados</Chip>
                  </div>
                </div>
              )}

              {conteudo()}
            </div>
          </div>
        </div>
      </div>

      {/* Distrato em modal próprio (nunca `window.confirm` — at-24): o motivo é
          obrigatório, e o `ModalConfirmacao` é só para sim/não. */}
      <ModalDistrato
        aberto={paraCancelar !== null}
        mensagem={
          paraCancelar
            ? `O contrato de ${paraCancelar.associado ?? 'associado'} (${paraCancelar.placa ?? 'sem placa'}) passa a constar como cancelado. O documento continua guardado.`
            : ''
        }
        processando={cancelando}
        onConfirmar={(motivo) => void confirmarCancelamento(motivo)}
        onCancelar={() => setParaCancelar(null)}
      />
    </ShellSaac>
  );
};

export default ContratosPage;
